import React from "react";
import StoreSidebar from "../components/StoreSidebar";
import { motion } from "framer-motion";
import {
  TrendingUp,
  Package,
  DollarSign,
  Store,
} from "lucide-react";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  CartesianGrid,
} from "recharts";

export default function Reports() {

  // 📊 MOCK DATA (can be replaced with API later)
  const salesData = [
    { day: "Mon", sales: 4000, profit: 1200 },
    { day: "Tue", sales: 3000, profit: 900 },
    { day: "Wed", sales: 5000, profit: 1800 },
    { day: "Thu", sales: 4780, profit: 1600 },
    { day: "Fri", sales: 5890, profit: 2100 },
    { day: "Sat", sales: 6390, profit: 2400 },
    { day: "Sun", sales: 7000, profit: 3000 },
  ];

  const reports = [
    {
      title: "Daily Sales",
      value: "₹12,500",
      icon: TrendingUp,
      color: "from-green-500 to-emerald-600",
      desc: "+12% growth trend",
    },
    {
      title: "Inventory",
      value: "342 Items",
      icon: Package,
      color: "from-blue-500 to-cyan-600",
      desc: "8 low stock alerts",
    },
    {
      title: "Profit",
      value: "₹4,800",
      icon: DollarSign,
      color: "from-purple-500 to-pink-600",
      desc: "Net margin stable",
    },
    {
      title: "Store Score",
      value: "92%",
      icon: Store,
      color: "from-orange-500 to-red-500",
      desc: "Operational efficiency",
    },
  ];

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">

      {/* LEFT SIDEBAR */}
      <div className="fixed left-0 top-0 h-screen w-72 border-r border-white/10">
        <StoreSidebar />
      </div>

      {/* MAIN */}
      <div className="ml-72 flex-1 p-6 lg:p-8">

        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold">
            📈 Analytics Command Center
          </h1>
          <p className="text-slate-400 mt-2">
            Real-time business intelligence dashboard
          </p>
        </motion.div>

        {/* KPI CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">

          {reports.map((item, i) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05, y: -5 }}
                className={`relative overflow-hidden p-6 rounded-3xl bg-gradient-to-r ${item.color}`}
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full" />

                <Icon size={28} />

                <h2 className="text-2xl font-bold mt-3">
                  {item.value}
                </h2>

                <p className="font-semibold">{item.title}</p>
                <p className="text-white/80 text-sm mt-1">
                  {item.desc}
                </p>
              </motion.div>
            );
          })}

        </div>

        {/* CHART SECTION */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* SALES LINE CHART */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/5 border border-white/10 rounded-3xl p-6"
          >
            <h3 className="text-xl font-bold mb-4">
              📊 Weekly Sales Trend
            </h3>

            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="day" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke="#22c55e"
                  strokeWidth={3}
                />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>

          {/* PROFIT BAR CHART */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/5 border border-white/10 rounded-3xl p-6"
          >
            <h3 className="text-xl font-bold mb-4">
              💰 Profit Distribution
            </h3>

            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="day" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip />
                <Bar dataKey="profit" fill="#a855f7" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

        </div>

        {/* INSIGHT PANEL */}
        <div className="mt-6 bg-white/5 border border-white/10 rounded-3xl p-6">
          <h3 className="text-lg font-bold mb-2">
            🧠 AI Insight Engine
          </h3>

          <p className="text-slate-400 text-sm">
            Sales peaked on Saturday and Sunday. Recommend increasing inventory
            before weekend rush. Profit margin is stable but mid-week dip
            detected on Tuesday.
          </p>
        </div>

      </div>
    </div>
  );
}